import {
  SwapInterface,
  SwapTokenPair,
  SwapProvider,
  SwapInfo,
  SwapMode,
  SwapStep,
} from '../interfaces/swapInterface'
import { Actor, ActorSubclass, HttpAgent } from '@dfinity/agent'
import { idlFactory as idlSwapFactory } from '../idls/kongswap/kongswapBackend.did'
import type { PoolReply, TxId, _SERVICE as SwapFactory } from '../idls/kongswap/kongswapBackend.did'
import { Principal } from '@dfinity/principal'
import { ICToken } from '../interfaces/common'

const KONG_BACKEND_CANISTER_ID = '2ipq2-uqaaa-aaaar-qailq-cai'

export interface PoolUnusedBalance {
  balance0: number
  balance1: number
}

interface KongSwapInterface extends SwapInterface<ICToken> {
  getQuoteKong: (
    mode: SwapMode,
    amount: number,
    token0Address: string,
    token1Address: string
  ) => Promise<SwapInfo>
}

class KongSwap implements KongSwapInterface {
  public agent: HttpAgent
  public swapFactory: ActorSubclass<SwapFactory>

  constructor(agent: HttpAgent) {
    this.agent = agent
    this.swapFactory = Actor.createActor(idlSwapFactory, {
      canisterId: KONG_BACKEND_CANISTER_ID,
      agent: this.agent,
    }) as ActorSubclass<SwapFactory>
  }

  async getPair(from: ICToken, to: ICToken) {
    const swapAmountResponse = await this.swapFactory.swap_amounts(
      'IC.' + from.address,
      BigInt(1),
      'IC.' + to.address
    )

    if ('Ok' in swapAmountResponse) {
      const amountResponse = swapAmountResponse.Ok

      return {
        swapCanisterPrincipal: 'N/A',
        feeRate: amountResponse.price,
        token0: {
          principal: amountResponse.pay_address,
          standard: '-',
        },
        token1: {
          principal: amountResponse.receive_address,
          standard: '-',
        },
        provider: SwapProvider.KONGSWAP,
        paused: false,
      }
    }

    if ('Err' in swapAmountResponse) {
      return Promise.reject(swapAmountResponse.Err)
    }

    return Promise.reject('Unknown response')
  }

  async getQuoteKong(
    _mode: SwapMode,
    amount: number,
    token0Address?: string,
    token1Address?: string
  ) {
    const response = await this.swapFactory.swap_amounts(
      'IC.' + token0Address,
      BigInt(amount),
      'IC.' + token1Address
    )

    if ('Ok' in response) {
      const info: SwapInfo = {
        quote: Number(response.Ok.receive_amount),
        slippage: Number(response.Ok.slippage),
      }
      return info
    }

    if ('Err' in response) {
      return Promise.reject(response.Err)
    }

    return Promise.reject('Unknown error')
  }

  constructorTxId(block_index: string): TxId {
    return {
      BlockIndex: BigInt(block_index),
    }
  }

  async swap(
    block_index: string,
    _mode: SwapMode,
    fromToken: ICToken,
    toToken: ICToken,
    amount: number,
    limitPrice: number,
    maxSlippage: number,
    setStep: (step: SwapStep) => void
  ) {
    const swapResponse = await this.swapFactory.swap({
      receive_token: 'IC.' + toToken.address,
      max_slippage: [maxSlippage],
      referred_by: [],
      receive_amount: [BigInt(limitPrice)],
      receive_address: [],
      pay_token: 'IC.' + fromToken.address,
      pay_amount: BigInt(amount),
      pay_tx_id: block_index === undefined ? [] : [this.constructorTxId(block_index)],
    })

    if ('Err' in swapResponse) {
      setStep(SwapStep.ErrorFallback)
      return Promise.reject(swapResponse.Err)
    }

    if ('Ok' in swapResponse) {
      return Number(swapResponse.Ok.receive_amount)
    }

    return Promise.reject('Unknown status')
  }

  async getPairs() {
    const pools = await this.swapFactory.pools([])

    if ('Ok' in pools) {
      return pools.Ok.map(this.parsePool.bind(this))
    }

    if ('Err' in pools) {
      return Promise.reject(pools.Err)
    }

    return []
  }

  parsePool(pool: PoolReply): SwapTokenPair {
    return {
      swapCanisterPrincipal: pool.pool_id.toString(),
      feeRate: Number(pool.lp_fee_bps),
      token0: {
        principal: pool.address_0,
        standard: pool.chain_0,
      },
      token1: {
        principal: pool.address_1,
        standard: pool.chain_1,
      },
      provider: SwapProvider.KONGSWAP,
      paused: false,
    }
  }

  async getPoolUnusedBalance(pool: SwapTokenPair, userPrincipal: string) {
    return Promise.reject('Not supported')
  }
  async getQuote(swapCanisterPrincipal: string, mode: SwapMode, amount: number) {
    return Promise.reject('Not supported')
  }
  getDepositAddress(swapCanisterPrincipal: string, principal: Principal) {
    return Promise.reject('Not supported')
  }
  getSwapPairEvents(swapPairCanisterPrincipal: string, accountPrincipal: string) {
    return Promise.reject('Not supported')
  }
  fallback(swapCanisterPrincipal: string, subaccount: number[]) {
    return Promise.reject('Not supported')
  }
}

export default KongSwap
